import moment from 'moment'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import DataTable, { TableColumn } from 'react-data-table-component'
import useAppToast from 'src/hooks/useAppToast'
import TableLoadingShell from 'src/utils/TableLoadingShell'
import { Form, Spinner } from 'react-bootstrap'
import StatusButton from '../orders/StatusButton'
import StatusButtonAdmin from './StatusButtonAdmin'
import { KTIcon, KTSVG } from 'src/marketConnect/helpers'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import {
  deleteUserAsync,
  getBillingPlansAsync,
  getCustomerChannelsAsync,
  getConnectedPlatformsAsync,
  getCustomersAsync,
  getSubscriptionTimelineAsync,
  loginAsCustomerAsync,
  updateCustomerStatusAsync,
  updateCustomerStatusunlockAsync,

} from 'src/redux/actions/customerAction'
import { UserActionsCell } from './UserActionCell'
import { CUSTOMER } from 'src/app/_interfaces/customer.interface'
import CommonAlertModal from 'src/utils/modals/CommonAlertModal'
import {
  accessSessionTokenKeyName,
  refreshSessionTokenKeyName,
} from 'src/utils/constants'
import {
  canStartImpersonation,
  startImpersonation,
} from 'src/utils/impersonationTokens'
import { useNavigate } from 'react-router-dom'
import { setLoggingData } from 'src/redux/slices/authSlice'
import { fetchUserAccountDetailsAsync } from 'src/redux/actions/accountActions'
import { clearFetchedUserAccountData } from 'src/redux/slices/fetchUserAccountDataSlice'
import { messageTemplete } from 'src/utils/messageTemplete'
import EnableProductModule from './EnableProductModule'
import CommonProceedAlertModal from 'src/utils/modals/CommonProceedAlertModal'
import CustomersFilterBar from './components/CustomersFilterBar'
import SendCustomerEmailModal, { SendEmailRecipient } from './SendCustomerEmailModal'
import {
  areSameArrays,
  BillingPlanOption,
  buildBillingPlanFilterOptions,
  buildPlatformFilterOptions,
  ConnectedPlatformOption,
  FilterOption,
  getResponseList,
} from './utils/customersFilterHelpers'
import {
  clearPersistedCustomerFilters,
  loadPersistedCustomerFilters,
  savePersistedCustomerFilters,
} from './utils/customersFilterStorage'
import CustomerBillingTimeline from './CustomerBillingTimeline'
import { clearTimelineData } from 'src/redux/slices/customerSlice'
import { SubscriptionTimelineOrder } from 'src/redux/actions/customerAction'
import './manage-customers-filters.scss'

const DEFAULT_PAGINATION_DATA = {
  page_limit: 25,
  page_num: 1,
  order_by: '-id',
  search: '',
  user_type: 'USER',
  page: 1,
  from_date: '',
  to_date: '',
  sync_from_date: '',
  sync_to_date: '',
  source: '',
  platform_ids: [] as string[],
  billing_plan_id: '',
  is_active: '',
}

type SelectedCustomerSnapshot = {
  status: boolean
  email: string
  name: string
}

const CommonColumn: FC<{ rowData: string }> = ({ rowData }) => {
  return (
    <div className='row'>
      <p className='d-inline-block  ' style={{ maxWidth: '180px' }}>
        {rowData}
      </p>
    </div>
  )
}

const ManageCustomers: FC = () => {
  const [searchString, setSearchString] = useState('')
  const [selectedPlatforms, setSelectedPlatforms] = useState<FilterOption[]>([])
  const [connectedPlatforms, setConnectedPlatforms] = useState<ConnectedPlatformOption[]>([])
  const [billingPlans, setBillingPlans] = useState<BillingPlanOption[]>([])

  const { successToast, errorToast, warningToast } = useAppToast()

  const [showDateRangePicker, setShowDateRangePicker] = useState(false)
  const [showDateRangePickerForOrders, setShowDateRangePickerForOrders] = useState(false)

  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const {
    customerList,
    customerLoader,
    totalCustomer,
    timelineLoading,
    timelineData,
    channelFilterOptions,
  } = useAppSelector((state) => state.customer)

  //for delete user
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({ current: 0, total: 0 })

  //used for update status
  const [confirmUpdateStatus, setConfirmUpdateStatus] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  // -------

  //used for login as customer
  const [confirmLoginAsUser, setConfirmLoginAsUser] = useState(false)
  const [loggingIn, setLoggingIn] = useState(false)
  //------

  //used for update status unlock
  const [confirmUpdateStatusunlock, setConfirmUpdateStatusunlock] = useState(false)
  const [updatingStatusUnlock, setUpdatingStatusUnlock] = useState(false)

  const [selectedUserData, setSelectedUserData] = useState<null | CUSTOMER>(null)
  const [selectedCustomersMap, setSelectedCustomersMap] = useState<Record<number, SelectedCustomerSnapshot>>({})
  const selectedCustomerIds = useMemo(
    () => Object.keys(selectedCustomersMap).map((id) => Number(id)),
    [selectedCustomersMap]
  )
  const [showSendEmailModal, setShowSendEmailModal] = useState(false)
  const [sendEmailRecipients, setSendEmailRecipients] = useState<SendEmailRecipient[]>([])
  const [showBillingTimeline, setShowBillingTimeline] = useState(false)
  const [timelineOrder, setTimelineOrder] = useState<SubscriptionTimelineOrder>('desc')
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION_DATA)
  const [filtersHydrated, setFiltersHydrated] = useState(false)
  const skipNextSaveRef = useRef(false)
  const pendingPlatformIdsRef = useRef<string[] | null>(null)

  const { fetchedUserAccountData, fetchedUserStaffData } = useAppSelector((state) => state.userAccountData)
  const isStaffAccount = fetchedUserAccountData?.account_type === "USER_STAFF"
  console.log("isStaffAccount", isStaffAccount)
  const isSuperAdminUser =
    fetchedUserAccountData?.account_type === 'SUPERADMIN' || fetchedUserAccountData?.is_superuser
  const rawCustomerAccess = fetchedUserAccountData?.role?.customer_access
  const customerAccessLevel = rawCustomerAccess ? rawCustomerAccess.toUpperCase() : null
  const hasCustomerAccessValue = customerAccessLevel !== null
  const canViewCustomers =
    isSuperAdminUser || customerAccessLevel === 'WRITE' || customerAccessLevel === 'READ' || customerAccessLevel === 'SUPERADMIN'
  const canModifyCustomers = isSuperAdminUser || customerAccessLevel === 'WRITE' || customerAccessLevel === 'SUPERADMIN'
  const denyCustomerAccess = hasCustomerAccessValue && !canViewCustomers && !isSuperAdminUser
  const rawReportAccess = fetchedUserAccountData?.role?.report_access
  const reportAccessLevel = rawReportAccess ? rawReportAccess.toUpperCase() : null
  const canAccessReports =
    isSuperAdminUser || reportAccessLevel === 'WRITE' || reportAccessLevel === 'READ'
  const isUserStaff = fetchedUserAccountData?.account_type === 'USER_STAFF';
  const isRead = fetchedUserAccountData?.role?.customer_access === 'READ';
  const canDeleteAccess = isSuperAdminUser || customerAccessLevel === 'SUPERADMIN'
  const canViewBillingHistory =
    isSuperAdminUser ||
    customerAccessLevel === 'WRITE' ||
    customerAccessLevel === 'SUPERADMIN' ||
    customerAccessLevel === 'FULL'
  const billingPlanFilterOptions = useMemo(
    () => buildBillingPlanFilterOptions(billingPlans),
    [billingPlans]
  )
  const platformFilterOptions = useMemo(
    () => buildPlatformFilterOptions(connectedPlatforms),
    [connectedPlatforms]
  )
  const sourceFilterOptions = useMemo(() => channelFilterOptions || [], [channelFilterOptions])
  const canSendCustomerEmail =
    !isUserStaff &&
    (isSuperAdminUser ||
      customerAccessLevel === 'WRITE' ||
      customerAccessLevel === 'SUPERADMIN')
  const showSelectionColumn = canSendCustomerEmail || canDeleteAccess
  const hasBulkSelection = selectedCustomerIds.length > 0
  const isInactiveFilter = paginationData.is_active === 'false'
  const selectedCustomers = useMemo(
    () =>
      selectedCustomerIds.map((id) => ({
        id,
        ...selectedCustomersMap[id],
      })),
    [selectedCustomerIds, selectedCustomersMap]
  )
  const updateStatusAsync = (data: CUSTOMER) => {
    setUpdatingStatus(true)
    dispatch(
      updateCustomerStatusAsync({
        user_id: data.id,
        status: data.status === true ? 'inactive' : 'active',
      })
    )
      .then((res) => {
        setUpdatingStatus(false)
        successToast(res.payload?.massage)
        setConfirmUpdateStatus(false)
        setSelectedUserData(null)
        dispatch(getCustomersAsync({ ...paginationData }))
      })
      .catch(() => {
        setUpdatingStatus(false)
        dispatch(getCustomersAsync({ ...paginationData }))
      })
  }

  const loginCustomerAsync = (data: CUSTOMER) => {
    const adminAccess = localStorage.getItem(accessSessionTokenKeyName)
    const adminRefresh = localStorage.getItem(refreshSessionTokenKeyName)
    const preflight = canStartImpersonation(adminAccess, adminRefresh)

    if (!preflight.ok) {
      warningToast(preflight.reason)
      setConfirmLoginAsUser(false)
      setSelectedUserData(null)
      return
    }

    setLoggingIn(true)
    dispatch(loginAsCustomerAsync({ user_id: data.id }))
      .then((res) => {
        if (res.payload?.status_code == '200') {
          const { access, refresh } = res.payload?.data ?? {}
          if (access && refresh && adminAccess && adminRefresh) {
            startImpersonation({
              customerAccess: access,
              customerRefresh: refresh,
              adminAccess,
              adminRefresh,
            })
            dispatch(clearFetchedUserAccountData())
            dispatch(fetchUserAccountDetailsAsync()).then(() => {
              navigate('/dashboard', { replace: true })
            })
          } else {
            warningToast('Unauthorized access')
          }
        }
        setLoggingIn(false)
        successToast(messageTemplete.LOGIN_SUCCESS)
        setConfirmLoginAsUser(false)
        setSelectedUserData(null)
      })
      .catch(() => {
        setLoggingIn(false)
        setConfirmLoginAsUser(false)
        dispatch(getCustomersAsync({ ...paginationData }))
      })
  }

  const openBillingTimeline = (customer: CUSTOMER, order: SubscriptionTimelineOrder = timelineOrder) => {
    setSelectedUserData(customer)
    setShowBillingTimeline(true)
    setTimelineOrder(order)
    dispatch(getSubscriptionTimelineAsync({ userId: customer.id, order }))
  }

  const handleTimelineOrderChange = (order: SubscriptionTimelineOrder) => {
    if (!selectedUserData || order === timelineOrder) return
    setTimelineOrder(order)
    dispatch(getSubscriptionTimelineAsync({ userId: selectedUserData.id, order }))
  }

  const closeBillingTimeline = () => {
    setShowBillingTimeline(false)
    setSelectedUserData(null)
    setTimelineOrder('desc')
    dispatch(clearTimelineData())
  }

  const updateStatusunlockAsync = (data: CUSTOMER) => {
    setUpdatingStatusUnlock(true)
    dispatch(
      updateCustomerStatusunlockAsync({
        user_id: data.id,
        status: "unlock"
      })
    )
      .then((res) => {
        setUpdatingStatusUnlock(false)
        successToast(res.payload?.massage)
        setConfirmUpdateStatusunlock(false)
        setSelectedUserData(null)
        dispatch(getCustomersAsync({ ...paginationData }))
      })
      .catch(() => {
        setUpdatingStatusUnlock(false)
        dispatch(getCustomersAsync({ ...paginationData }))
      })
  }

  const buildSendEmailRecipients = (customerIds: number[]): SendEmailRecipient[] => {
    return customerIds.map((id) => {
      const customer = customerList.find((item) => item.id === id)
      const snapshot = selectedCustomersMap[id]
      return {
        id,
        name: customer?.name?.trim() || snapshot?.name?.trim() || `Customer #${id}`,
        email: customer?.email || snapshot?.email || '—',
      }
    })
  }

  const openSendEmailModal = (customerIds: number[]) => {
    if (!customerIds.length) {
      warningToast('Select at least one customer to send email.')
      return
    }
    setSendEmailRecipients(buildSendEmailRecipients(customerIds))
    setShowSendEmailModal(true)
  }

  const toggleCustomerSelection = (customer: CUSTOMER) => {
    setSelectedCustomersMap((prev) => {
      if (prev[customer.id]) {
        const next = { ...prev }
        delete next[customer.id]
        return next
      }

      return {
        ...prev,
        [customer.id]: {
          status: customer.status,
          email: customer.email,
          name: customer.name ?? '',
        },
      }
    })
  }

  const allPageCustomersSelected =
    customerList.length > 0 && customerList.every((customer) => selectedCustomerIds.includes(customer.id))

  const toggleSelectAllCustomers = () => {
    if (allPageCustomersSelected) {
      setSelectedCustomersMap((prev) => {
        const next = { ...prev }
        customerList.forEach((customer) => {
          delete next[customer.id]
        })
        return next
      })
      return
    }

    setSelectedCustomersMap((prev) => {
      const next = { ...prev }
      customerList.forEach((customer) => {
        next[customer.id] = {
          status: customer.status,
          email: customer.email,
          name: customer.name ?? '',
        }
      })
      return next
    })
  }

  const clearCustomerSelection = () => {
    setSelectedCustomersMap({})
  }

  const openBulkDeleteModal = () => {
    if (!selectedCustomerIds.length) {
      warningToast('Select at least one customer to delete.')
      return
    }

    setConfirmBulkDelete(true)
  }

  const deleteBulkCustomers = async () => {
    const idsToDelete = [...selectedCustomerIds]
    setBulkDeleting(true)
    setBulkDeleteProgress({ current: 0, total: idsToDelete.length })

    let successCount = 0
    let failCount = 0

    for (let index = 0; index < idsToDelete.length; index += 1) {
      try {
        const res = await dispatch(deleteUserAsync({ user_id: idsToDelete[index] })).unwrap()
        if (res.status_code === 200) {
          successCount += 1
        } else {
          failCount += 1
        }
      } catch {
        failCount += 1
      }

      setBulkDeleteProgress({ current: index + 1, total: idsToDelete.length })
    }

    setBulkDeleting(false)
    setConfirmBulkDelete(false)
    setSelectedCustomersMap({})
    dispatch(getCustomersAsync({ ...paginationData }))

    if (successCount > 0) {
      successToast(`Deleted ${successCount} customer${successCount === 1 ? '' : 's'}.`)
    }
    if (failCount > 0) {
      warningToast(`Failed to delete ${failCount} customer${failCount === 1 ? '' : 's'}.`)
    }
  }

  const getBulkDeleteSummary = () => {
    const previewNames = selectedCustomers
      .slice(0, 3)
      .map((customer) => customer.email || customer.name || `Customer #${customer.id}`)
    const remainingCount = Math.max(selectedCustomers.length - previewNames.length, 0)

    if (!previewNames.length) {
      return ''
    }

    if (remainingCount === 0) {
      return previewNames.join(', ')
    }

    return `${previewNames.join(', ')} and ${remainingCount} more`
  }

  const renderSelectCheckbox = (
    checked: boolean,
    onChange: () => void,
    ariaLabel: string
  ) => (
    <div className='manage-customers-select-cell'>
      <Form.Check
        type='checkbox'
        className='manage-customers-checkbox'
        checked={checked}
        onChange={onChange}
        aria-label={ariaLabel}
      />
    </div>
  )

  const selectionColumn: TableColumn<CUSTOMER> = {
    id: 'select',
    name: renderSelectCheckbox(
      allPageCustomersSelected,
      toggleSelectAllCustomers,
      'Select all customers on this page'
    ),
    cell: (row) =>
      renderSelectCheckbox(
        selectedCustomerIds.includes(row.id),
        () => toggleCustomerSelection(row),
        `Select ${row.name || row.email}`
      ),
    width: '56px',
    minWidth: '56px',
    maxWidth: '56px',
  }

  const actionColumn: TableColumn<CUSTOMER> = {
    id: 'action',
    name: 'Action',
    cell: (row) => (
      <UserActionsCell
        customerDetail={row}
        canModify={canModifyCustomers}
        canView={canViewCustomers}
        canViewReport={canAccessReports}
        canDeleteAccess={canDeleteAccess}
        canViewBillingHistory={canViewBillingHistory}
        hideIndividualDelete={hasBulkSelection}
        onSendEmail={
          canSendCustomerEmail ? () => openSendEmailModal([row.id]) : undefined
        }
        onViewBillingHistory={
          canViewBillingHistory ? () => openBillingTimeline(row) : undefined
        }
        onDeleteCustomer={() => {
          setSelectedUserData(row)
          setConfirmDeleteUser(true)
        }}
        onLoginAsCustomer={() => {
          setConfirmLoginAsUser(true)
          setSelectedUserData(row)
        }}
        updateCustomerStatus={() => {
          setConfirmUpdateStatus(true)
          setSelectedUserData(row)
        }}
        updateCustomerStatusunlockAsync={() => {
          setConfirmUpdateStatusunlock(true)
          setSelectedUserData(row)
        }}
      />
    ),
    minWidth: '200px',
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
  }

  const staffActionColumn: TableColumn<CUSTOMER> = {
    name: 'Action',
    cell: (row) => (
      <div className='manage-customers-actions d-flex align-items-center gap-1 justify-content-center'>
        <a
          href="#"
          className="btn btn-sm fw-bold btn-hover-border"
          style={{
            color: '#0d6efd',
            backgroundColor: '#e7f1ff',
            border: '1px solid transparent',
            transition: 'border-color 0.2s',
            padding: '6px 14px',
            textDecoration: 'none',
          }}
          data-kt-menu-trigger="click"
          data-kt-menu-placement="bottom-end"
          onClick={(e) => {
            e.preventDefault()
            setConfirmLoginAsUser(true)
            setSelectedUserData(row)
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.borderColor = '#0d6efd';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLAnchorElement).style.borderColor = 'transparent';
          }}
        >
          Login
          {!isUserStaff || isRead && <KTIcon iconName="down" className="fs-5 m-0" />}
        </a>
      </div>
    ),
    minWidth: '140px',
    center: true,
  }

  const leadingColumns: TableColumn<CUSTOMER>[] = (() => {
    if (isUserStaff || isRead) {
      return [staffActionColumn]
    }
    if (showSelectionColumn) {
      return [selectionColumn, actionColumn]
    }
    return [actionColumn]
  })()

  const columns: TableColumn<CUSTOMER>[] = [
    ...leadingColumns,

    {
      name: !isUserStaff ? 'Customer' : 'Organization Owner',
      cell: (row) => (row ? <CommonColumn rowData={row.name ?? ''} /> : null),
      minWidth: '140px',
    },

    {
      name: 'Email',
      selector: (row) => row.email,
      cell: (row) => (row ? <CommonColumn rowData={row.email} /> : null),
      minWidth: '200px',
    },

    ...(!isUserStaff
      ? [
        {
          name: 'Status',
          selector: (row) => row.status,
          cell: (row) => {
            if (row.user_locked) {
              return (
                <span className='badge badge-light-warning text-warning fs-8 fw-semibold'>
                  Locked
                </span>
              )
            }

            return (
              <span
                className={`badge fs-8 fw-semibold ${row.status
                    ? 'badge-light-success text-success'
                    : 'badge-light-danger text-danger'
                  }`}
              >
                {row.status ? 'Active' : 'Inactive'}
              </span>
            )
          },
          center: true,
          minWidth: '120px',
        },
        {
          name: 'Source',
          selector: (row) => row.source,
          cell: (row) => (
            row ? <CommonColumn rowData={row?.source ?? 'COMMERCIUM'} /> : null
          ),
          minWidth: '160px',
        },
        {
          name: 'Current Plan',
          cell: (row) => (
            <div>
              <small>
                {row?.user_plan_name ?? 'No Plan Active'}
              </small>
              <br />
              <small>
                {row?.user_plan_price && row?.user_plan_price_currency
                  ? `${row.user_plan_price} ${row.user_plan_price_currency}`
                  : ''}
                <span style={{ fontSize: '9px' }}>
                  {row?.user_plan_billing_frequency
                    ? ` / ${row.user_plan_billing_frequency}`
                    : ''}
                </span>
              </small>
            </div>
          ),
          minWidth: '140px',
        },
        {
          name: 'Joined On (UTC)',
          cell: (row) =>
            row.sign_up
              ? moment.utc(row.sign_up).format('hh:mm A, DD-MMM-YYYY')
              : null,
          center: true,
          minWidth: '170px',
        },
      ]
      : []),

    ...(!isUserStaff
      ? [
        {
          name: 'Sync Count',
          cell: (row) => (
            <div>
              <p>
                Orders:&nbsp;
                <span className="fw-bold">
                  {row?.synced_order_count}
                </span>
              </p>
              <p>
                Products:&nbsp;
                <span className="fw-bold">
                  {row?.synced_product_count}
                </span>
              </p>
            </div>
          ),
          center: true,
          minWidth: '180px',
        },
      ]
      : []),

    ...(!isUserStaff
      ? [
        {
          name: 'Country',
          selector: (row) => row.country,
          cell: (row) => (
            <CommonColumn rowData={row?.country || 'N/A'} />
          ),
          minWidth: '200px',
        },
      ]
      : []),

    //  SAME POSITION: Phone Number
    ...(!isUserStaff
      ? [
        {
          name: 'Phone Number',
          selector: (row) => row.phone_number,
          cell: (row) => (
            <CommonColumn
              rowData={
                !row?.phone_number ||
                  row.phone_number === '+' ||
                  row.phone_number === ''
                  ? 'N/A'
                  : row.phone_number
              }
            />
          ),
          minWidth: '200px',
        },
        {
          name: 'Last Login (UTC)',
          cell: (row) =>
            row?.last_login
              ? moment.utc(row.last_login).format('hh:mm A, DD-MM-YYYY')
              : null,
          minWidth: '160px',
        },
      ]
      : []),

    ...((!isUserStaff && !isRead)
      ? [
        {
          name: 'Enable Product Module',
          cell: (row) => (
            <EnableProductModule
              isEnableProductModule={row.isEnableProductModule}
              customer={row}
            />
          ),
          minWidth: '200px',
        },
      ]
      : []),
  ];


  const handleResetFilters = () => {
    setPaginationData(DEFAULT_PAGINATION_DATA)
    setSearchString('')
    setSelectedPlatforms([])
    pendingPlatformIdsRef.current = null
    clearPersistedCustomerFilters(fetchedUserAccountData?.id)
  }

  useEffect(() => {
    if (filtersHydrated) {
      return
    }

    if (fetchedUserAccountData == null) {
      return
    }

    const userId = fetchedUserAccountData.id
    if (userId) {
      const persistedFilters = loadPersistedCustomerFilters(userId)
      if (persistedFilters) {
        skipNextSaveRef.current = true
        setSearchString(persistedFilters.search)
        setPaginationData((previousPaginationData) => ({
          ...previousPaginationData,
          search: persistedFilters.search,
          platform_ids: persistedFilters.platform_ids,
          billing_plan_id: persistedFilters.billing_plan_id,
          is_active: persistedFilters.is_active,
          source: persistedFilters.source,
          from_date: persistedFilters.from_date,
          to_date: persistedFilters.to_date,
          sync_from_date: persistedFilters.sync_from_date,
          sync_to_date: persistedFilters.sync_to_date,
          page: 1,
          page_num: 1,
        }))

        if (persistedFilters.platform_ids.length) {
          pendingPlatformIdsRef.current = persistedFilters.platform_ids
        }
      }
    }

    setFiltersHydrated(true)
  }, [fetchedUserAccountData, filtersHydrated])

  useEffect(() => {
    if (!filtersHydrated) {
      return
    }

    const userId = fetchedUserAccountData?.id
    if (!userId) {
      return
    }

    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false
      return
    }

    savePersistedCustomerFilters(userId, {
      search: searchString,
      platform_ids: paginationData.platform_ids,
      billing_plan_id: paginationData.billing_plan_id,
      is_active: paginationData.is_active,
      source: paginationData.source,
      from_date: paginationData.from_date,
      to_date: paginationData.to_date,
      sync_from_date: paginationData.sync_from_date,
      sync_to_date: paginationData.sync_to_date,
    })
  }, [
    filtersHydrated,
    fetchedUserAccountData?.id,
    searchString,
    paginationData.platform_ids,
    paginationData.billing_plan_id,
    paginationData.is_active,
    paginationData.source,
    paginationData.from_date,
    paginationData.to_date,
    paginationData.sync_from_date,
    paginationData.sync_to_date,
  ])

  useEffect(() => {
    if (!pendingPlatformIdsRef.current?.length || !platformFilterOptions.length) {
      return
    }

    const platformIds = pendingPlatformIdsRef.current
    pendingPlatformIdsRef.current = null
    const matchedPlatforms = platformFilterOptions.filter((option) =>
      platformIds.includes(option.value)
    )
    setSelectedPlatforms(matchedPlatforms)
  }, [platformFilterOptions])

  useEffect(() => {
    if (denyCustomerAccess || isUserStaff) {
      return
    }

    dispatch(getBillingPlansAsync({}))
      .then((response) => {
        setBillingPlans(getResponseList(response.payload?.data))
      })
      .catch(() => {
        setBillingPlans([])
      })

    dispatch(getConnectedPlatformsAsync({}))
      .then((response) => {
        setConnectedPlatforms(getResponseList(response.payload?.data))
      })
      .catch(() => {
        setConnectedPlatforms([])
      })
  }, [dispatch, denyCustomerAccess, isUserStaff])

  useEffect(() => {
    if (denyCustomerAccess || isUserStaff) {
      return
    }

    if (Array.isArray(channelFilterOptions) && channelFilterOptions.length > 0) {
      return
    }

    dispatch(getCustomerChannelsAsync())
  }, [dispatch, denyCustomerAccess, isUserStaff, channelFilterOptions])

  useEffect(() => {
    const availablePlatformValues = new Set(platformFilterOptions.map((option) => option.value))
    const nextSelectedPlatforms = selectedPlatforms.filter((option) =>
      availablePlatformValues.has(option.value)
    )
    const nextPlatformIds = nextSelectedPlatforms.map((option) => option.value)

    if (nextSelectedPlatforms.length !== selectedPlatforms.length) {
      setSelectedPlatforms(nextSelectedPlatforms)
    }

    setPaginationData((previousPaginationData) =>
      areSameArrays(previousPaginationData.platform_ids, nextPlatformIds)
        ? previousPaginationData
        : {
          ...previousPaginationData,
          platform_ids: nextPlatformIds,
        }
    )
  }, [platformFilterOptions, selectedPlatforms])

  useEffect(() => {
    if (denyCustomerAccess || fetchedUserAccountData == null || !filtersHydrated) {
      return
    }
    dispatch(getCustomersAsync({ ...paginationData }))
  }, [dispatch, paginationData, denyCustomerAccess, fetchedUserAccountData, filtersHydrated])

  if (denyCustomerAccess) {
    return (
      <div>
        <div className='card border-0 shadow rounded'>
          <div className='card-body py-10'>
            <p className='text-center text-muted mb-0'>
              You do not have permission to view customer data.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <>
        <div className='mb-2'>
          <span className='fw-bold text-lg ' style={{ fontSize: '16px' }}>
            {isStaffAccount ? "Select an Organization" : "Manage Customers"}
          </span>
        </div>
        {isStaffAccount && (<div className='mb-3'>
          <span className='text-lg' style={{ fontSize: '12px' }}>You are a member of the following Organizations. Please choose the one you wish to access.
          </span></div>)}

        <div className='card border-0 shadow rounded '>
          <CustomersFilterBar
            isUserStaff={isUserStaff}
            searchString={searchString}
            onSearchChange={setSearchString}
            onSearchSubmit={() =>
              setPaginationData((pre) => ({ ...pre, search: searchString, page: 1, page_num: 1 }))
            }
            selectedPlatforms={selectedPlatforms}
            platformOptions={platformFilterOptions}
            onPlatformsChange={(nextSelectedPlatforms) => {
              setSelectedPlatforms(nextSelectedPlatforms)
              setPaginationData((pre) => ({
                ...pre,
                platform_ids: nextSelectedPlatforms.map((option) => option.value),
                page: 1,
                page_num: 1,
              }))
            }}
            billingPlanOptions={billingPlanFilterOptions}
            selectedBillingPlanId={paginationData.billing_plan_id}
            onBillingPlanChange={(value) =>
              setPaginationData((pre) => ({ ...pre, billing_plan_id: value, page: 1, page_num: 1 }))
            }
            selectedStatus={paginationData.is_active}
            onStatusChange={(value) =>
              setPaginationData((pre) => ({ ...pre, is_active: value, page: 1, page_num: 1 }))
            }
            selectedSource={paginationData.source}
            sourceOptions={sourceFilterOptions}
            onSourceChange={(value) =>
              setPaginationData((pre) => ({ ...pre, source: value, page: 1, page_num: 1 }))
            }
            syncFromDate={paginationData.sync_from_date}
            syncToDate={paginationData.sync_to_date}
            showDateRangePickerForOrders={showDateRangePickerForOrders}
            onToggleOrdersDate={() => setShowDateRangePickerForOrders((pre) => !pre)}
            onCloseOrdersDate={() => setShowDateRangePickerForOrders(false)}
            onPickOrdersDateRange={(startDate, endDate) =>
              setPaginationData((pre) => ({
                ...pre,
                sync_from_date: moment(startDate).format('YYYY-MM-DD'),
                sync_to_date: moment(endDate).format('YYYY-MM-DD'),
                page: 1,
                page_num: 1,
              }))
            }
            fromDate={paginationData.from_date}
            toDate={paginationData.to_date}
            showDateRangePicker={showDateRangePicker}
            onToggleCustomerDate={() => setShowDateRangePicker((pre) => !pre)}
            onCloseCustomerDate={() => setShowDateRangePicker(false)}
            onPickCustomerDateRange={(startDate, endDate) =>
              setPaginationData((pre) => ({
                ...pre,
                from_date: moment(startDate).format('YYYY-MM-DD'),
                to_date: moment(endDate).format('YYYY-MM-DD'),
                page: 1,
                page_num: 1,
              }))
            }
            onResetFilters={handleResetFilters}
            canSendCustomerEmail={canSendCustomerEmail}
            canDeleteAccess={canDeleteAccess}
            selectedCustomerCount={selectedCustomerIds.length}
            onSendEmail={() => openSendEmailModal(selectedCustomerIds)}
            onBulkDelete={openBulkDeleteModal}
          />

          <div className='manage-customers-table-shell'>
            {hasBulkSelection ? (
              <div className='manage-customers-bulk-bar d-flex flex-wrap align-items-center justify-content-between gap-3 px-4 py-3 bg-light-danger'>
                <div className='d-flex flex-wrap align-items-center gap-2'>
                  <KTIcon iconName='profile-user' className='fs-4 text-danger' />
                  <span className='fs-7 fw-semibold text-danger'>
                    {selectedCustomerIds.length} customer{selectedCustomerIds.length === 1 ? '' : 's'} selected
                  </span>
                  {isInactiveFilter ? (
                    <span className='badge badge-light-danger text-danger fs-8 fw-semibold'>
                      Inactive filter
                    </span>
                  ) : null}
                </div>

                <button
                  type='button'
                  className='btn btn-sm btn-light fw-bold fs-8'
                  onClick={clearCustomerSelection}
                >
                  Clear selection
                </button>
              </div>
            ) : null}

            <TableLoadingShell loading={customerLoader} minHeight="70vh">
            <DataTable
              className='manage-customers-table'
              fixedHeader
              persistTableHead
              fixedHeaderScrollHeight={
                hasBulkSelection ? 'calc(70vh - 3.25rem)' : '70vh'
              }
              key={paginationData.page_limit + ''}
              data={customerList}
              columns={columns}
              //   onSort={handleSort}
              paginationTotalRows={totalCustomer}
              responsive
              pagination
              paginationServer
              progressPending={false}
              paginationPerPage={paginationData.page_limit}
              paginationResetDefaultPage
              onChangePage={(pageNumber, totalRows) => {
                setPaginationData((pre) => {
                  return { ...pre, page_num: pageNumber }
                })
              }}
              onChangeRowsPerPage={(currentPage) => {
                setPaginationData((pre) => {
                  return { ...pre, page_limit: currentPage }
                })
              }}
              conditionalRowStyles={[
                {
                  when: (row) => selectedCustomerIds.includes(row.id),
                  style: {
                    backgroundColor: 'rgba(62, 151, 255, 0.08)',
                  },
                },
              ]}
              customStyles={{
                headCells: {
                  style: { fontWeight: 'bold', fontSize: '14px' },
                },
                cells: {
                  style: {},
                },
              }}
            />
            </TableLoadingShell>
          </div>
          <CommonAlertModal
            key={'status change modal'}
            title={` You are ${selectedUserData?.status === true ? 'Deactivating' : 'Activating'
              } the ${selectedUserData?.name ? selectedUserData.name : 'customer'
              }. Do you want to continue?`}
            subtitle='Press "Confirm" for continue'
            successBtnTitle={updatingStatus ? 'Updating...' : 'Confirm'}
            successDisabled={updatingStatus}
            isVisible={confirmUpdateStatus}
            onSuccess={() => {
              if (selectedUserData) {
                updateStatusAsync(selectedUserData)
              }
            }}
            onCancel={() => {
              setConfirmUpdateStatus(false)
              setSelectedUserData(null)
            }}
            isTermsVisible={false}
          />

          {!isStaffAccount ? (<CommonAlertModal
            key={'login modal'}
            title={`You are logging in as ${selectedUserData?.name ? selectedUserData.name : 'customer'
              }, do you want to continue?`}
            subtitle='Press "Confirm" for continue'
            successBtnTitle={loggingIn ? 'Logging in...' : 'Confirm'}
            successDisabled={loggingIn}
            isVisible={confirmLoginAsUser}
            onSuccess={() => {
              if (selectedUserData) {
                loginCustomerAsync(selectedUserData)
              }
            }}
            onCancel={() => {
              setConfirmLoginAsUser(false)
              setSelectedUserData(null)
            }}
            isTermsVisible={false}
          />) : (
            <CommonProceedAlertModal
              isVisible={confirmLoginAsUser}
              customerName={selectedUserData?.name ?? undefined}
              loading={loggingIn}
              onConfirm={() => {
                if (selectedUserData) {
                  loginCustomerAsync(selectedUserData)
                }
              }}
              onCancel={() => {
                setConfirmLoginAsUser(false)
                setSelectedUserData(null)
              }}
            />
          )}

          <CommonAlertModal
            key={'Unlock User Modal'}
            title={` You are
            unlocking ${selectedUserData?.name ? selectedUserData.name : 'customer'
              }. Do you want to continue?`}
            subtitle='Press "Confirm" for continue'
            successBtnTitle={updatingStatusUnlock ? 'Updating...' : 'Confirm'}
            successDisabled={updatingStatusUnlock}
            isVisible={confirmUpdateStatusunlock}
            onSuccess={() => {
              if (selectedUserData) {
                updateStatusunlockAsync(selectedUserData)
              }
            }}
            onCancel={() => {
              setConfirmUpdateStatusunlock(false)
              setSelectedUserData(null)
            }}
            isTermsVisible={false}
          />


          <SendCustomerEmailModal
            show={showSendEmailModal}
            recipients={sendEmailRecipients}
            customerFilters={{
              is_active: paginationData.is_active,
              billing_plan_id: paginationData.billing_plan_id,
            }}
            onClose={() => {
              setShowSendEmailModal(false)
              setSendEmailRecipients([])
            }}
            onSent={() => {
              setSelectedCustomersMap({})
            }}
          />

          <CommonAlertModal
            key={'delete modal'}
            title={`Are you sure? Want to delete ${selectedUserData?.name ? selectedUserData.name : 'customer'
              }, do you want to continue?`}
            subtitle='Press "Confirm" for continue'
            successBtnTitle={deleting ? 'Deleting...' : 'Confirm Delete'}
            successDisabled={deleting}
            isVisible={confirmDeleteUser}
            onSuccess={() => {
              if (selectedUserData) {
                //delete functionality
                setDeleting(true)
                dispatch(deleteUserAsync({ user_id: selectedUserData.id }))
                  .unwrap()
                  .then((res) => {
                    if (res.status_code === 200) {
                      successToast(res.massage);
                      setConfirmDeleteUser(false);
                      setSelectedUserData(null);
                    } else {
                      warningToast(res?.data?.massage);
                    }
                    dispatch(getCustomersAsync({ ...paginationData }));
                    setDeleting(false);
                  })
                  .catch((err) => {
                    warningToast(err?.data?.detail);
                    setDeleting(false);
                  });
              }
            }}
            onCancel={() => {
              setConfirmDeleteUser(false)
              setSelectedUserData(null)
            }}
            isTermsVisible={false}
          />

          <CustomerBillingTimeline
            show={showBillingTimeline}
            customerName={selectedUserData?.name ?? null}
            loading={timelineLoading}
            data={timelineData}
            order={timelineOrder}
            onOrderChange={handleTimelineOrderChange}
            onClose={closeBillingTimeline}
          />
          <CommonAlertModal
            key={'bulk delete modal'}
            title={`Delete ${selectedCustomerIds.length} customer${selectedCustomerIds.length === 1 ? '' : 's'}?`}
            subtitle={
              bulkDeleting
                ? `Deleting ${bulkDeleteProgress.current} of ${bulkDeleteProgress.total}...`
                : `This will permanently delete: ${getBulkDeleteSummary()}. Press "Confirm" to continue.`
            }
            successBtnTitle={
              bulkDeleting
                ? `Deleting ${bulkDeleteProgress.current}/${bulkDeleteProgress.total}...`
                : 'Confirm delete'
            }
            successDisabled={bulkDeleting}
            isVisible={confirmBulkDelete}
            onSuccess={() => {
              deleteBulkCustomers()
            }}
            onCancel={() => {
              if (!bulkDeleting) {
                setConfirmBulkDelete(false)
              }
            }}
            isTermsVisible={false}
          />
        </div>
      </>
    </div>
  )
}

export default ManageCustomers
